'use client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Card } from '../ui/card';
import type { AddressSummaryResponse } from '@/lib/hooks/useAPI';
import { formatTokenValue } from '@/lib/formatters';

interface TokenListProps {
  userTokens: AddressSummaryResponse['tokens'];
}

export function TokenList({ userTokens }: TokenListProps) {
  const router = useRouter();

  // Helper function to calculate actual token amount based on decimals
  const getActualTokenAmount = (balance: number, decimals: number) => {
    return balance / 10 ** decimals;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">{userTokens?.length} Tokens</h3>
      </div>
      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Token</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {userTokens?.length && userTokens?.length > 0 ? (
              userTokens?.map((token) => {
                // Calculate actual token amount based on decimals
                const actualBalance = getActualTokenAmount(
                  token?.balance ?? 0,
                  token?.decimals ?? 0
                );

                // Calculate price per token (USD value divided by token amount)
                const pricePerToken = (token?.valueUsd ?? 0) / actualBalance;

                return (
                  <TableRow
                    key={token?.pubkey}
                    onClick={() => {
                      router.push(`/token/${token?.pubkey}`);
                    }}
                    className="cursor-pointer"
                  >
                    <TableCell>
                      <Link
                        href={`/token/${token?.pubkey}`}
                        className="font-medium hover:underline"
                      >
                        {token?.name}
                      </Link>
                      <div className="text-xs text-muted-foreground">{token?.ticker}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {actualBalance === 0 || actualBalance >= 0.01
                        ? actualBalance.toLocaleString()
                        : actualBalance.toFixed(
                            Math.max(8, token?.decimals ? Math.min(token.decimals, 20) : 8)
                          )}{' '}
                      {token?.ticker}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatTokenValue(pricePerToken)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatTokenValue(token?.valueUsd)}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center">
                  No tokens found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
